import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CommonNetworkProfile,
  LicenceProfileTypesEdit,
  ListResponse,
  ProfileNote,
  ProfileNotes,
  ProfileResponse,
  ProfileTypesEdit,
} from '@shared/schemas';

import type { NoteOperation } from '../domain/profile-detail.model';
import type { TypeCategories } from '../domain/profile-detail.rules';
import {
  emptyAddToList,
  emptyContactStatus,
  emptyDeactivate,
  emptyEnrich,
  emptyExpertValidation,
  emptyLicenceTypesEdit,
  emptyNotes,
  emptyReactivate,
  emptyTypesEdit,
  initialState,
  type NoteSave,
  type ProfileDetailState,
} from './profile-detail.state';

/** Payload of the notes transitions (#676): which profile the notes belong to. */
export interface NotesLoaded {
  profileId: string;
  notes: ProfileNotes;
}

export interface NotesFailed {
  profileId: string;
  message: string;
}

/** Payload of an added or edited note (#743): the note as the server saved it. */
export interface NoteSaved {
  profileId: string;
  note: ProfileNote;
}

/** Payload of a note change that started (#743): on which profile, what, on which note. */
export interface NoteSaveStarted {
  profileId: string;
  operation: NoteOperation;
  noteId: string | null;
}

/**
 * Payload of a failed note change (#743): the whole change, from the saga, so
 * the reducer does not depend on what the store still holds. `noteGone`: the
 * server answered that the note no longer exists (404 Note), so it leaves the
 * list.
 */
export interface NoteSaveFailed {
  profileId: string;
  operation: NoteOperation;
  noteId: string | null;
  message: string;
  noteGone: boolean;
}

/** Payload of a deleted note (#743). */
export interface NoteDeleted {
  profileId: string;
  noteId: string;
}

/**
 * Settle the profile's change. Its Notes tab, when open (`attached`), gets the
 * outcome to react to; a detached change is only dropped, so nothing is
 * replayed when the reader comes back. No pending change: nothing to settle.
 */
function settle(state: ProfileDetailState, profileId: string, outcome: Omit<NoteSave, 'attached'>): void {
  const current = state.notes.saves[profileId];
  if (current?.status !== 'pending') return;
  if (current.attached) state.notes.saves[profileId] = { ...outcome, attached: true };
  else delete state.notes.saves[profileId];
}

const succeeded = (change: Pick<NoteSave, 'operation' | 'noteId'>): Omit<NoteSave, 'attached'> => ({
  status: 'succeeded',
  ...change,
  error: null,
  noteGone: false,
});

/**
 * Only the changes still in flight, detached: what outlives a page reset. The
 * page's tabs are gone with it; the Notes tab re-attaches when it mounts.
 */
function inFlight(saves: Record<string, NoteSave>): Record<string, NoteSave> {
  return Object.fromEntries(
    Object.entries(saves)
      .filter(([, save]) => save.status === 'pending')
      .map(([profileId, save]) => [profileId, { ...save, attached: false }]),
  );
}

/**
 * Profile Detail slice. Reducers are pure state transitions only; the initial
 * load, the silent refresh and the enrich POST all live in the saga, which
 * dispatches these `load*` / `enrich*` transitions as it progresses.
 * `loadStarted` keeps the current `profile` so a re-load (refresh after a tag
 * edit or a completed enrichment) never blanks the page. `favoriteChanged`
 * patches the in-place favourite flag from the shared `FavoriteButton`. The
 * Network tab has its own load pair, driven on each entry into that tab.
 */
const slice = createSlice({
  name: 'profileDetail',
  initialState,
  reducers: {
    // ── Profile load lifecycle ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<ProfileResponse>) => {
      state.profile = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Enrichment lifecycle ──
    /** POST is in flight. */
    enrichStarted: (state) => {
      state.enrich = { status: 'enriching', error: null };
    },
    /** POST accepted: the live `EnrichmentPanel` drawer opens and takes over. */
    enrichAccepted: (state) => {
      state.enrich = { status: 'accepted', error: null };
    },
    enrichFailed: (state, action: PayloadAction<string>) => {
      state.enrich = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled enrichment (page calls after the panel closes). */
    enrichCleared: (state) => {
      state.enrich = emptyEnrich;
    },

    // ── Deactivation lifecycle ──
    /** POST is in flight. */
    deactivateStarted: (state) => {
      state.deactivate = { status: 'pending', error: null };
    },
    deactivateFailed: (state, action: PayloadAction<string>) => {
      state.deactivate = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled deactivation (page calls after the snackbar). */
    deactivateCleared: (state) => {
      state.deactivate = emptyDeactivate;
    },

    // ── Reactivation lifecycle (#659) ──
    /** POST is in flight. */
    reactivateStarted: (state) => {
      state.reactivate = { status: 'pending', error: null, immunised: false };
    },
    /** `immunised`: the profile won't be re-deactivated by future scans. */
    reactivateSucceeded: (state, action: PayloadAction<{ immunised: boolean }>) => {
      state.reactivate = { status: 'succeeded', error: null, immunised: action.payload.immunised };
    },
    reactivateFailed: (state, action: PayloadAction<string>) => {
      state.reactivate = { status: 'failed', error: action.payload, immunised: false };
    },
    /** Acknowledge a settled reactivation (page calls after the snackbar). */
    reactivateCleared: (state) => {
      state.reactivate = emptyReactivate;
    },

    // ── Admin contact verification (#556) ──
    /** PATCH is in flight for one contact; the other toggle stays usable. */
    contactStatusStarted: (state, action: PayloadAction<'email' | 'phone'>) => {
      state.contactStatus = { pending: action.payload, error: null };
    },
    contactStatusFailed: (state, action: PayloadAction<string>) => {
      state.contactStatus = { pending: null, error: action.payload };
    },
    /** Settled; the reload carries the new status, so there is nothing to hold. */
    contactStatusCleared: (state) => {
      state.contactStatus = emptyContactStatus;
    },

    // ── Admin "Verified by Invyo Expert" check (#754) ──
    /** POST and reload in flight for this profile: its badge is disabled. */
    expertValidationStarted: (state, action: PayloadAction<string>) => {
      state.expertValidation = { status: 'pending', profileId: action.payload, error: null };
    },
    /** The badge did not move, or the profile could not be reloaded; the message is for the snackbar. */
    expertValidationFailed: (state, action: PayloadAction<{ profileId: string; message: string }>) => {
      state.expertValidation = {
        status: 'failed',
        profileId: action.payload.profileId,
        error: action.payload.message,
      };
    },
    /**
     * Settled; the reloaded profile carries the new badge. With an id, only
     * that profile's run is settled: another one started since keeps going.
     */
    expertValidationCleared: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload && state.expertValidation.profileId !== action.payload) return;
      state.expertValidation = emptyExpertValidation;
    },

    // ── Network tab ──
    commonNetworkLoadStarted: (state) => {
      state.commonNetwork.loading = true;
    },
    /** Also the failure transition; a failed load lands an empty, settled tab. */
    commonNetworkLoaded: (state, action: PayloadAction<CommonNetworkProfile[]>) => {
      state.commonNetwork = { rows: action.payload, loading: false, loaded: true };
    },

    // ── Add to list (admin, #614) ──
    assignableListsLoadStarted: (state) => {
      state.addToList.listsStatus = 'loading';
    },
    assignableListsLoaded: (state, action: PayloadAction<ListResponse[]>) => {
      state.addToList.lists = action.payload;
      state.addToList.listsStatus = 'loaded';
    },
    // ── Type chip (admin, #629) ──
    typeCategoriesLoaded: (state, action: PayloadAction<TypeCategories>) => {
      state.typeCategories = action.payload;
    },

    // ── The admin's "Types" dialog (#652) ──
    typesEditLoadStarted: (state) => {
      state.typesEdit = { ...emptyTypesEdit, status: 'loading' };
    },
    typesEditLoadFailed: (state) => {
      state.typesEdit = { ...emptyTypesEdit, status: 'failed' };
    },
    typesEditStarted: (state, action: PayloadAction<string>) => {
      state.typesEdit.pendingType = action.payload;
      state.typesEdit.error = null;
    },
    /** Every answer carries the profile's types afterwards: loaded, removed or restored. */
    typesEditLoaded: (state, action: PayloadAction<ProfileTypesEdit>) => {
      state.typesEdit = { status: 'loaded', data: action.payload, pendingType: null, error: null };
    },
    typesEditFailed: (state, action: PayloadAction<string>) => {
      state.typesEdit.pendingType = null;
      state.typesEdit.error = action.payload;
    },
    typesEditClosed: (state) => {
      state.typesEdit = emptyTypesEdit;
    },

    // ── A client's "Types" dialog, for their licence (#723) ──
    licenceTypesEditLoadStarted: (state) => {
      state.licenceTypesEdit = { ...emptyLicenceTypesEdit, status: 'loading' };
    },
    licenceTypesEditLoadFailed: (state) => {
      state.licenceTypesEdit = { ...emptyLicenceTypesEdit, status: 'failed' };
    },
    licenceTypesEditStarted: (state, action: PayloadAction<string>) => {
      state.licenceTypesEdit.pendingType = action.payload;
      state.licenceTypesEdit.error = null;
    },
    /** Every answer carries the profile's types afterwards: loaded, added or removed. */
    licenceTypesEditLoaded: (state, action: PayloadAction<LicenceProfileTypesEdit>) => {
      state.licenceTypesEdit = { status: 'loaded', data: action.payload, pendingType: null, error: null };
    },
    licenceTypesEditFailed: (state, action: PayloadAction<string>) => {
      state.licenceTypesEdit.pendingType = null;
      state.licenceTypesEdit.error = action.payload;
    },
    licenceTypesEditClosed: (state) => {
      state.licenceTypesEdit = emptyLicenceTypesEdit;
    },
    assignableListsFailed: (state) => {
      state.addToList.listsStatus = 'failed';
    },
    addToListStarted: (state) => {
      state.addToList = { ...state.addToList, pending: true, error: null, added: null };
    },
    addToListSucceeded: (state, action: PayloadAction<string>) => {
      state.addToList = { ...state.addToList, pending: false, added: action.payload };
    },
    addToListFailed: (state, action: PayloadAction<string>) => {
      state.addToList = { ...state.addToList, pending: false, error: action.payload };
    },
    /** Acknowledge a settled POST (page calls after the snackbar); keeps the loaded lists. */
    addToListCleared: (state) => {
      state.addToList = { ...emptyAddToList, lists: state.addToList.lists, listsStatus: state.addToList.listsStatus };
    },

    // ── Notes tab (#676) ──
    // A new profile starts from nothing; the same profile keeps what it shows, so
    // re-opening the tab refreshes the notes without flashing the spinner. The
    // changes in flight stay: each is keyed by its own profile (#743), so the
    // tab of the new one never reads them. The page's `resetForm` keeps the
    // ones still in flight, so a tab back on their profile shows them busy.
    notesLoadStarted: (state, action: PayloadAction<string>) => {
      if (state.notes.profileId !== action.payload) {
        state.notes = { ...emptyNotes, profileId: action.payload, saves: state.notes.saves };
      }
      state.notes.status = 'loading';
      state.notes.error = null;
    },
    notesLoadSucceeded: (state, action: PayloadAction<NotesLoaded>) => {
      if (state.notes.profileId !== action.payload.profileId) return;
      state.notes.notes = action.payload.notes;
      state.notes.status = 'loaded';
    },
    notesLoadFailed: (state, action: PayloadAction<NotesFailed>) => {
      if (state.notes.profileId !== action.payload.profileId) return;
      state.notes.status = 'failed';
      state.notes.error = action.payload.message;
    },
    notesSaveStarted: (state, action: PayloadAction<NoteSaveStarted>) => {
      const { profileId, operation, noteId } = action.payload;
      state.notes.saves[profileId] = { status: 'pending', operation, noteId, error: null, noteGone: false, attached: true };
    },
    /** The profile's Notes tab mounted: it takes the outcome of a change still in flight. */
    notesTabAttached: (state, action: PayloadAction<string>) => {
      const save = state.notes.saves[action.payload];
      if (save?.status === 'pending') save.attached = true;
    },
    /**
     * The profile's Notes tab unmounted: a change in flight stays, detached (its
     * outcome will not be shown); a settled one is dropped.
     */
    notesTabDetached: (state, action: PayloadAction<string>) => {
      const save = state.notes.saves[action.payload];
      if (save?.status === 'pending') save.attached = false;
      else delete state.notes.saves[action.payload];
    },
    // The note as the server saved it (normalised, author and dates) joins the
    // list or replaces its old copy; on the notes of its own profile only.
    noteAdded: (state, action: PayloadAction<NoteSaved>) => {
      const { profileId, note } = action.payload;
      settle(state, profileId, succeeded({ operation: 'add', noteId: null }));
      if (state.notes.profileId !== profileId || !state.notes.notes) return;
      // By id: a reload that answered after the POST already holds it.
      const list = state.notes.notes.notes;
      const i = list.findIndex((n) => n.id === note.id);
      if (i >= 0) list[i] = note;
      else list.unshift(note);
    },
    noteUpdated: (state, action: PayloadAction<NoteSaved>) => {
      const { profileId, note } = action.payload;
      settle(state, profileId, succeeded({ operation: 'update', noteId: note.id }));
      if (state.notes.profileId !== profileId || !state.notes.notes) return;
      const i = state.notes.notes.notes.findIndex((n) => n.id === note.id);
      if (i >= 0) state.notes.notes.notes[i] = note;
    },
    noteDeleted: (state, action: PayloadAction<NoteDeleted>) => {
      const { profileId, noteId } = action.payload;
      settle(state, profileId, succeeded({ operation: 'delete', noteId }));
      if (state.notes.profileId !== profileId || !state.notes.notes) return;
      state.notes.notes.notes = state.notes.notes.notes.filter((n) => n.id !== noteId);
    },
    // A note gone from the server (404 Note) leaves the list too, on the notes
    // of its own profile only; any other failure leaves the list as it is. The
    // saga announces the failure itself; the entry only drives the tab.
    notesSaveFailed: (state, action: PayloadAction<NoteSaveFailed>) => {
      const { profileId, operation, noteId, message, noteGone } = action.payload;
      settle(state, profileId, { status: 'failed', operation, noteId, error: message, noteGone });
      if (!noteGone || noteId === null || state.notes.profileId !== profileId || !state.notes.notes) return;
      state.notes.notes.notes = state.notes.notes.notes.filter((n) => n.id !== noteId);
    },
    /** The profile's change is handled, or its tab unmounted: back to idle. */
    notesSaveCleared: (state, action: PayloadAction<string>) => {
      delete state.notes.saves[action.payload];
    },

    /** Patch the favourite flag in place (shared FavoriteButton onChange). */
    favoriteChanged: (state, action: PayloadAction<boolean>) => {
      if (state.profile) state.profile.is_favorite = action.payload;
    },

    /**
     * Reset everything to the initial state (on mount + unmount), except the
     * note changes still in flight (detached): their requests run on, and the
     * tab back on their profile must show them busy (#743).
     */
    resetForm: (state) => ({ ...initialState, notes: { ...emptyNotes, saves: inFlight(state.notes.saves) } }),
  },
});

export const profileDetailActions = slice.actions;
export const profileDetailReducer = slice.reducer;
